
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

export const ExportOptions = () => {
  const [isExporting, setIsExporting] = useState<string | null>(null);

  const generateCSVContent = () => {
    const headers = ['Metric', 'Value', 'Date', 'Category'];
    const sampleData = [
      ['Revenue', '50000', '2024-01-01', 'Financial'],
      ['Users', '1250', '2024-01-01', 'Growth'],
      ['Conversion Rate', '3.5%', '2024-01-01', 'Performance'],
      ['Customer Satisfaction', '4.2', '2024-01-01', 'Quality'],
      ['Monthly Active Users', '2100', '2024-01-01', 'Engagement']
    ];
    
    const csvContent = [headers, ...sampleData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    return csvContent;
  };

  const generateExcelContent = () => {
    // Simple tab-separated values for Excel compatibility
    const headers = ['Metric\tValue\tDate\tCategory\tTrend'];
    const sampleData = [
      'Revenue\t$50,000\t2024-01-01\tFinancial\t↑ 12%',
      'Users\t1,250\t2024-01-01\tGrowth\t↑ 8%',
      'Conversion Rate\t3.5%\t2024-01-01\tPerformance\t↓ 2%',
      'Customer Satisfaction\t4.2/5\t2024-01-01\tQuality\t↑ 5%',
      'Monthly Active Users\t2,100\t2024-01-01\tEngagement\t↑ 15%'
    ];
    
    return [headers, ...sampleData].join('\n');
  };

  const generatePDFContent = () => {
    return `
Dashboard Export Report
Generated on: ${new Date().toLocaleDateString()}

=== METRICS SUMMARY ===
Revenue: $50,000 (↑ 12%)
Users: 1,250 (↑ 8%)
Conversion Rate: 3.5% (↓ 2%)
Customer Satisfaction: 4.2/5 (↑ 5%)
Monthly Active Users: 2,100 (↑ 15%)

=== KEY INSIGHTS ===
- Revenue growth continues strong trajectory
- User acquisition pace is accelerating
- Minor conversion rate dip requires attention
- Customer satisfaction remains high
- Engagement metrics showing positive trends

=== RECOMMENDATIONS ===
- Focus on conversion optimization
- Maintain current growth strategies
- Monitor customer feedback closely
- Expand engagement initiatives

Report generated by Dashboard Analytics System
`;
  };

  const triggerDownload = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format: string) => {
    setIsExporting(format);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to export data");
        return;
      }

      // Create export record
      const { data, error } = await supabase
        .from("exports")
        .insert([
          {
            user_id: user.id,
            resource_type: "dashboard",
            resource_id: crypto.randomUUID(),
            format,
            status: "pending"
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Export error:", error);
        toast.error("Failed to initiate export");
        return;
      }

      // Show processing message
      toast.info(`Processing ${format.toUpperCase()} export...`);

      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate content based on format
      let content = '';
      let mimeType = '';
      let fileExtension = '';

      switch (format) {
        case 'csv':
          content = generateCSVContent();
          mimeType = 'text/csv';
          fileExtension = 'csv';
          break;
        case 'excel':
          content = generateExcelContent();
          mimeType = 'application/vnd.ms-excel';
          fileExtension = 'xls';
          break;
        case 'pdf':
          content = generatePDFContent();
          mimeType = 'text/plain'; // Simplified PDF as text
          fileExtension = 'txt'; // Will be treated as PDF report
          break;
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `dashboard-export-${timestamp}.${fileExtension}`;

      // Update export status to completed
      const downloadUrl = `${window.location.origin}/exports/${data.id}.${fileExtension}`;
      
      const { error: updateError } = await supabase
        .from("exports")
        .update({ 
          status: "completed", 
          download_url: downloadUrl,
          updated_at: new Date().toISOString()
        })
        .eq("id", data.id);

      if (updateError) {
        console.error("Update error:", updateError);
        toast.error("Failed to complete export");
        return;
      }

      // Trigger download
      triggerDownload(content, filename, mimeType);

      toast.success(`${format.toUpperCase()} export completed and downloaded!`);

      // Log export action
      await supabase
        .from('system_logs')
        .insert({
          action: 'data_export',
          status: 'success',
          description: `Dashboard data exported in ${format.toUpperCase()} format`,
          metadata: { 
            export_id: data.id, 
            format,
            user_id: user.id,
            filename,
            rows_exported: format === 'csv' ? 5 : 'N/A'
          }
        });

    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(null);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold">Export Options</h2>
          <p className="text-sm text-muted-foreground">
            Export your dashboard data in different formats
          </p>
        </div>
        <Download className="h-6 w-6 text-muted-foreground" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="outline"
          className="justify-start"
          onClick={() => handleExport("csv")}
          disabled={isExporting !== null}
        >
          {isExporting === "csv" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-2 h-4 w-4" />
          )}
          {isExporting === "csv" ? "Exporting..." : "Export as CSV"}
        </Button>
        
        <Button
          variant="outline"
          className="justify-start"
          onClick={() => handleExport("excel")}
          disabled={isExporting !== null}
        >
          {isExporting === "excel" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="mr-2 h-4 w-4" />
          )}
          {isExporting === "excel" ? "Exporting..." : "Export as Excel"}
        </Button>
        
        <Button
          variant="outline"
          className="justify-start"
          onClick={() => handleExport("pdf")}
          disabled={isExporting !== null}
        >
          {isExporting === "pdf" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileText className="mr-2 h-4 w-4" />
          )}
          {isExporting === "pdf" ? "Exporting..." : "Export as PDF"}
        </Button>
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 <strong>Tip:</strong> Exports include all current dashboard data, metrics, and visualizations. 
          Large datasets may take a few moments to process.
        </p>
      </div>
    </Card>
  );
};
